import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Zap, RefreshCw, Loader2, Save, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";

function pctColor(p) {
  if (p == null) return "bg-brand-emerald";
  if (p >= 100) return "bg-destructive";
  if (p >= 80) return "bg-brand-gold";
  return "bg-brand-emerald";
}

function CreditsBar({ usage, limit, percent, tall = false }) {
  const pct = Math.min(100, percent ?? (limit ? Math.round((usage / limit.value) * 100) : 0));
  const showBar = limit != null;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-brand-ink">
          {usage} <span className="text-stone-400 font-normal">/</span>{" "}
          {limit ? limit.value : (
            <span className="inline-flex items-center gap-0.5 text-stone-500 font-normal">
              <InfinityIcon className="h-3.5 w-3.5" /> ilimitado
            </span>
          )}
        </span>
        {showBar && <span className="text-xs text-stone-500">{pct}%</span>}
      </div>
      {showBar && (
        <div className={`w-full ${tall ? "h-3" : "h-2"} rounded-full bg-stone-100 overflow-hidden`}>
          <div
            className={`h-full ${pctColor(percent)} rounded-full transition-all duration-500`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {limit && (
        <div className="text-[10px] text-stone-400 uppercase tracking-widest">
          Periodo: {limit.period === "week" ? "semanal" : "mensual"}
        </div>
      )}
    </div>
  );
}

export default function ConsumoIA() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null); // company_id → open dialog
  const [companyLimitEdit, setCompanyLimitEdit] = useState(null);
  const [userLimitEdit, setUserLimitEdit] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({ company_id: "all", user_id: "all" });
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/credits/consumption");
      setData(resp);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = {};
      if (historyFilter.company_id !== "all") params.company_id = historyFilter.company_id;
      if (historyFilter.user_id !== "all") params.user_id = historyFilter.user_id;
      const { data: rows } = await api.get("/credits/history", { params });
      setHistoryRows(rows);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setHistoryLoading(false); }
  };

  useEffect(() => { if (historyOpen) loadHistory(); }, [historyOpen, historyFilter.company_id, historyFilter.user_id]);

  const openCompanyLimit = (c) => {
    setCompanyLimitEdit({
      company_id: c.company_id,
      company_name: c.company_name,
      value: c.company_limit?.value ?? "",
      period: c.company_limit?.period ?? "month",
      unlimited: !c.company_limit,
    });
  };

  const saveCompanyLimit = async () => {
    try {
      const payload = {
        ai_credit_limit: companyLimitEdit.unlimited
          ? null
          : { value: Number(companyLimitEdit.value) || 0, period: companyLimitEdit.period },
      };
      await api.patch(`/companies/${companyLimitEdit.company_id}`, payload);
      toast.success("Límite actualizado");
      setCompanyLimitEdit(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const openUserLimit = (companyId, u) => {
    setUserLimitEdit({
      company_id: companyId,
      user_id: u.user_id,
      user_name: u.user_name,
      value: u.limit?.value ?? "",
      period: u.limit?.period ?? "month",
      unlimited: !u.limit,
    });
  };

  const saveUserLimit = async () => {
    try {
      // Necesitamos módulos actuales de la membresía → obtenerlos primero
      const { data: memberships } = await api.get(`/team/users/${userLimitEdit.user_id}/memberships`);
      const m = memberships.find((mm) => mm.company_id === userLimitEdit.company_id);
      if (!m) throw new Error("Membresía no encontrada");
      await api.post(`/team/users/${userLimitEdit.user_id}/memberships`, {
        company_id: userLimitEdit.company_id,
        modules: m.modules,
        ai_credit_limit: userLimitEdit.unlimited
          ? null
          : { value: Number(userLimitEdit.value) || 0, period: userLimitEdit.period },
      });
      toast.success("Límite de usuario actualizado");
      setUserLimitEdit(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Error");
    }
  };

  const allCompaniesForFilter = data.map((c) => ({ id: c.company_id, name: c.company_name }));
  const allUsersForFilter = useMemo(() => {
    const map = new Map();
    data.forEach((c) => (c.users || []).forEach((u) => map.set(u.user_id, u)));
    return Array.from(map.values());
  }, [data]);

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="consumo-ia-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <Zap className="h-8 w-8 text-brand-gold" />
            Consumo IA
          </h1>
          <p className="text-sm text-stone-500 mt-2 max-w-3xl">
            Créditos consumidos por empresa y por usuario en el periodo actual. 1 generación = 1 crédito.
            Configure límites reversibles por empresa o por miembro. Sin límite = ilimitado.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="open-history-button">
            Ver historial de generaciones
          </Button>
          <Button variant="outline" onClick={load} data-testid="refresh-consumption-button">
            <RefreshCw className="h-4 w-4 mr-2" /> Recargar
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="card-elevated p-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
      ) : data.length === 0 ? (
        <div className="card-elevated p-16 text-center text-stone-500">Sin empresas todavía.</div>
      ) : (
        <div className="space-y-6">
          {data.map((c) => (
            <div key={c.company_id} className="card-elevated overflow-hidden" data-testid={`consumo-empresa-${c.company_id}`}>
              <div className="p-5 border-b border-border flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div
                    className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-bold shrink-0"
                    style={{ backgroundColor: c.company_color }}
                  >
                    {(c.company_name || "?")[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="font-serif-display text-xl sm:text-2xl text-brand-ink font-semibold truncate">{c.company_name}</div>
                      {!c.active && (
                        <Badge className="bg-stone-100 text-stone-500 border-stone-200 text-[10px]">Inactiva</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="w-full sm:w-56">
                  <CreditsBar
                    usage={c.company_usage}
                    limit={c.company_limit}
                    percent={c.company_percent}
                    tall
                  />
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => openCompanyLimit(c)}
                  data-testid={`edit-company-limit-${c.company_id}`}
                >
                  Ajustar límite
                </Button>
              </div>
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50/60">
                    <TableHead>Miembro</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Créditos</TableHead>
                    <TableHead>Límite</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(c.users || []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-stone-400 text-sm">Sin miembros</TableCell></TableRow>
                  ) : (
                    c.users.map((u) => (
                      <TableRow key={u.user_id}>
                        <TableCell>
                          <div className="text-sm font-medium text-brand-ink">{u.user_name}</div>
                          <div className="text-xs text-stone-500">{u.user_email}</div>
                        </TableCell>
                        <TableCell><Badge className="bg-stone-100 text-stone-700 border-stone-200 capitalize">{u.user_role}</Badge></TableCell>
                        <TableCell className="w-64">
                          <CreditsBar usage={u.usage} limit={u.limit} percent={u.percent} />
                        </TableCell>
                        <TableCell className="text-xs text-stone-500">
                          {u.limit ? `${u.limit.value} / ${u.limit.period === "week" ? "sem" : "mes"}` : "Sin límite"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => openUserLimit(c.company_id, u)}
                            data-testid={`edit-user-limit-${c.company_id}-${u.user_id}`}
                          >
                            Ajustar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Company limit dialog */}
      <Dialog open={!!companyLimitEdit} onOpenChange={(v) => !v && setCompanyLimitEdit(null)}>
        <DialogContent data-testid="company-limit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Límite de créditos IA · {companyLimitEdit?.company_name}</DialogTitle>
            <DialogDescription>Aplica a la empresa en su totalidad. Sin límite = ilimitado.</DialogDescription>
          </DialogHeader>
          {companyLimitEdit && (
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={companyLimitEdit.unlimited}
                  onChange={(e) => setCompanyLimitEdit({ ...companyLimitEdit, unlimited: e.target.checked })}
                  data-testid="company-limit-unlimited"
                />
                <span className="text-sm">Sin límite (ilimitado)</span>
              </label>
              {!companyLimitEdit.unlimited && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Valor</div>
                    <Input
                      type="number" min={0} value={companyLimitEdit.value}
                      onChange={(e) => setCompanyLimitEdit({ ...companyLimitEdit, value: e.target.value })}
                      data-testid="company-limit-value"
                    />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Periodo</div>
                    <Select value={companyLimitEdit.period}
                      onValueChange={(v) => setCompanyLimitEdit({ ...companyLimitEdit, period: v })}>
                      <SelectTrigger data-testid="company-limit-period"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">Mensual</SelectItem>
                        <SelectItem value="week">Semanal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompanyLimitEdit(null)}>Cancelar</Button>
            <Button
              onClick={saveCompanyLimit}
              className="bg-brand-emerald text-white hover:bg-brand-emerald-dark"
              data-testid="company-limit-save"
            >
              <Save className="h-4 w-4 mr-2" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User limit dialog */}
      <Dialog open={!!userLimitEdit} onOpenChange={(v) => !v && setUserLimitEdit(null)}>
        <DialogContent data-testid="user-limit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Límite personal · {userLimitEdit?.user_name}</DialogTitle>
            <DialogDescription>Aplica solo a este usuario dentro de esta empresa.</DialogDescription>
          </DialogHeader>
          {userLimitEdit && (
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={userLimitEdit.unlimited}
                  onChange={(e) => setUserLimitEdit({ ...userLimitEdit, unlimited: e.target.checked })}
                  data-testid="user-limit-unlimited"
                />
                <span className="text-sm">Sin límite personal (usa solo el de la empresa)</span>
              </label>
              {!userLimitEdit.unlimited && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Valor</div>
                    <Input
                      type="number" min={0} value={userLimitEdit.value}
                      onChange={(e) => setUserLimitEdit({ ...userLimitEdit, value: e.target.value })}
                      data-testid="user-limit-value"
                    />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Periodo</div>
                    <Select value={userLimitEdit.period}
                      onValueChange={(v) => setUserLimitEdit({ ...userLimitEdit, period: v })}>
                      <SelectTrigger data-testid="user-limit-period"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">Mensual</SelectItem>
                        <SelectItem value="week">Semanal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserLimitEdit(null)}>Cancelar</Button>
            <Button
              onClick={saveUserLimit}
              className="bg-brand-emerald text-white hover:bg-brand-emerald-dark"
              data-testid="user-limit-save"
            >
              <Save className="h-4 w-4 mr-2" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historial dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Historial de generaciones IA</DialogTitle>
            <DialogDescription>Filtre por empresa o usuario.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3">
            <Select value={historyFilter.company_id} onValueChange={(v) => setHistoryFilter({ ...historyFilter, company_id: v })}>
              <SelectTrigger className="flex-1" data-testid="history-filter-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                {allCompaniesForFilter.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={historyFilter.user_id} onValueChange={(v) => setHistoryFilter({ ...historyFilter, user_id: v })}>
              <SelectTrigger className="flex-1" data-testid="history-filter-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los usuarios</SelectItem>
                {allUsersForFilter.map((u) => (<SelectItem key={u.user_id} value={u.user_id}>{u.user_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="max-h-[55vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50/60">
                  <TableHead>Fecha</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Prompt</TableHead>
                  <TableHead>Créd.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyLoading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-brand-emerald" /></TableCell></TableRow>
                ) : historyRows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-stone-500">Sin generaciones registradas.</TableCell></TableRow>
                ) : (
                  historyRows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs text-stone-600 whitespace-nowrap">{new Date(r.timestamp).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })}</TableCell>
                      <TableCell className="text-sm">{r.company_name}</TableCell>
                      <TableCell className="text-sm">{r.user_name}</TableCell>
                      <TableCell className="text-xs text-stone-600 max-w-md truncate" title={r.prompt}>{r.prompt}</TableCell>
                      <TableCell><Badge className="bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40">{r.credits_used}</Badge></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Exportar componente CreditsBar para reuso en MotorCreativo
export { CreditsBar };
